import { default as Form } from 'antd/es/form';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { hasValue } from 'utils/hasValue';

import {
  HowItWorksCard,
  InstructionText,
  VerificationForm
} from '@pages/TOTPSetup';
import { usePostAuthenticationMfaValidateTotpFactor } from 'api';

import { setCredentials } from 'reduxLocal/slices/authSlice';

import { useAccessToken } from 'hooks/context';
import { useMfaSettings } from 'hooks/useMfaSettings';
import { useRedirectTo } from 'hooks/useRedirectTo';

export const TOTPValidate = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);

  const { navigateWithRedirectTo } = useRedirectTo();

  const { totpEnabled, loading, hasPhone } = useMfaSettings();

  const navigate = useNavigate();

  const { setAccessToken } = useAccessToken();

  const dispatch = useDispatch();

  const { mutate, isPending } = usePostAuthenticationMfaValidateTotpFactor({
    request: { withCredentials: true }
  });

  const onSubmit = (values: { code: string }) => {
    setError(null);
    mutate(
      { data: { auth_payload: values.code } },
      {
        onSuccess: (data) => {
          if (!hasValue(data?.data?.access_token)) {
            return;
          }
          // for v1 calls
          dispatch(setCredentials({ access_token: data.data.access_token }));
          // for v2 calls
          setAccessToken(data.data.access_token);
          onSuccess?.();
        },
        onError: () => {
          setError('TOTP validation failed. Please try again.');
        }
      }
    );
  };

  return (
    <div className="only-shadow flex justify-between gap-8 self-center bg-white p-8 md:size-fit md:rounded-2xl">
      <section className="flex w-1/2 flex-col gap-4">
        <h1 className="text-2xl font-semibold text-content-primary">
          Verify your identity
        </h1>
        <div className="flex flex-col items-start">
          <h3 className="text-lg font-medium">Enter verification code</h3>
          <InstructionText>
            Enter the 6-digit code from your authenticator app
          </InstructionText>
        </div>

        <VerificationForm
          form={form}
          error={error}
          isPending={isPending}
          onCancel={() => {
            if (totpEnabled && !hasPhone && !loading) {
              navigateWithRedirectTo('/');
            } else {
              navigate(-1);
            }
          }}
          onFinish={onSubmit}
        />
      </section>
      <div className="w-1/2">
        <HowItWorksCard />
      </div>
    </div>
  );
};
